import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import Header from '../Header';
import MaerskButton from '../common/MaerskButton';
import PlaygroundSidebar from '../common/PlaygroundSidebar';
import SystemMessageEditor from './SystemMessageEditor';
import { McButton } from '@maersk-global/mds-react-wrapper/components-core/mc-button';
import { McIcon } from '@maersk-global/mds-react-wrapper/components-core/mc-icon';
import { McInput } from '@maersk-global/mds-react-wrapper/components-core/mc-input';
import { McLoadingIndicator } from '@maersk-global/mds-react-wrapper/components-core/mc-loading-indicator';
import { COLORS, SPACING, BORDER_RADIUS } from '../../constants/theme';
import api from '../../services/api';

const SystemMessageManager = () => {
  const navigate = useNavigate();
  const { accounts } = useMsal();
  const userId = accounts[0]?.localAccountId || accounts[0]?.homeAccountId || '';
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [offset, setOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [filterFavourites, setFilterFavourites] = useState(false);
  const ITEMS_PER_PAGE = 20;

  // Load messages when filters change
  useEffect(() => {
    loadMessages(true);
  }, [searchTerm, filterFavourites]);

  // Listen for Neu assistant selecting or creating a system message
  useEffect(() => {
    const handleSelectMessage = (e) => {
      const msg = e.detail;
      if (msg && msg.id) {
        setSelectedMessage(msg);
        setIsCreating(false);
        setShowEditor(true);
      }
    };
    const handleCreateNew = () => {
      setSelectedMessage(null);
      setIsCreating(true);
      setShowEditor(true);
    };
    window.addEventListener('selectSystemMessage', handleSelectMessage);
    window.addEventListener('createNewSystemMessage', handleCreateNew);
    return () => {
      window.removeEventListener('selectSystemMessage', handleSelectMessage);
      window.removeEventListener('createNewSystemMessage', handleCreateNew);
    };
  }, []);

  const loadMessages = async (reset = false) => {
    if (reset) {
      setLoading(true);
      setOffset(0);
      setMessages([]);
    }

    try {
      const currentOffset = reset ? 0 : offset;
      const params = new URLSearchParams({
        offset: currentOffset,
        limit: ITEMS_PER_PAGE,
        search: searchTerm
      });
      if (userId) params.set('user_id', userId);
      if (filterFavourites) params.set('favourites', 'true');

      const response = await api.get(
        `/system-messages?${params.toString()}`
      );

      const data = response.data;

      if (reset) {
          setMessages(data.messages || []);
        } else {
          setMessages(prev => [...prev, ...(data.messages || [])]);
        }

        setTotalCount(data.total || 0);
        setHasMore((currentOffset + ITEMS_PER_PAGE) < (data.total || 0));
        setOffset(currentOffset + ITEMS_PER_PAGE);
    } catch (err) {
      console.error('Error loading system messages:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleFavourite = async (e, message) => {
    e.stopPropagation();
    // Optimistic update
    setMessages(prev => prev.map(m =>
      m.id === message.id ? { ...m, is_favourite: !m.is_favourite } : m
    ));
    try {
      await api.patch(`/system-messages/${message.id}/favourite`, { user_id: userId });
    } catch (err) {
      console.error('Failed to toggle favourite:', err);
      // Revert on error
      setMessages(prev => prev.map(m =>
        m.id === message.id ? { ...m, is_favourite: message.is_favourite } : m
      ));
    }
  };

  const handleCreateNew = () => {
    setSelectedMessage(null);
    setIsCreating(true);
    setShowEditor(true);
  };

  const handleEditMessage = async (message) => {
    setIsCreating(false);
    try {
      const response = await api.get(`/system-messages/${message.id}`);
      setSelectedMessage(response.data);
    } catch (err) {
      console.error('Failed to load full message:', err);
      setSelectedMessage(message); // fallback — editor will have empty content fields
    }
    setShowEditor(true);
  };

  const handleCloseEditor = () => {
    setShowEditor(false);
    setSelectedMessage(null);
    setIsCreating(false);
  };

  const handleSaveSuccess = () => {
    handleCloseEditor();
    loadMessages(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (showEditor) {
    return (
      <SystemMessageEditor
        message={selectedMessage}
        isCreating={isCreating}
        onClose={handleCloseEditor}
        onSaveSuccess={handleSaveSuccess}
      />
    );
  }

  return (
    <>
      {/* Main Content - Always render for blur background */}
      <div style={{
        minHeight: '100vh',
        backgroundColor: COLORS.background,
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
          <Header />
        </div>

        {/* Page body: sidebar + content */}
        <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <PlaygroundSidebar />
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, overflowY: 'auto' }}>

        {/* Main Content */}
        <div style={{
          flex: 1,
          maxWidth: '1400px',
          width: '100%',
          margin: '0 auto',
          padding: `${SPACING.xxl}px ${SPACING.xl}px`
        }}>
          {/* Page Title */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: SPACING.xxl
        }}>
          <h1 className="mds-headline--medium" style={{ color: COLORS.text, margin: 0 }}>
            Instructions
          </h1>
          <MaerskButton
            onClick={handleCreateNew}
            variant="primary"
            size="medium"
          >
            + Create New
          </MaerskButton>
        </div>
        {/* Search Bar */}
        <div style={{
          marginBottom: SPACING.xxl,
          display: 'flex',
          gap: SPACING.md,
          alignItems: 'center'
        }}>
          <div style={{ flex: 1 }}>
            <McInput
              label="Search instructions"
              hiddenlabel
              type="search"
              icon="magnifying-glass"
              placeholder="Search by name or description..."
              value={searchTerm}
              input={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <McButton
            label="Favourites"
            icon={filterFavourites ? 'star-solid' : 'star'}
            appearance="neutral"
            variant={filterFavourites ? 'filled' : 'outlined'}
            active={filterFavourites}
            click={() => setFilterFavourites(f => !f)}
          />
          <div className="mds-text--small-normal" style={{ color: COLORS.textSecondary, whiteSpace: 'nowrap' }}>
            {totalCount} {totalCount === 1 ? 'instruction' : 'instructions'}
          </div>
        </div>

        {/* Loading State */}
        {loading && messages.length === 0 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            padding: `${SPACING.xxxl * 2}px`
          }}>
            <McLoadingIndicator label="Loading instructions..." />
          </div>
        )}

        {/* Empty State */}
        {!loading && messages.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: `${SPACING.xxxl * 2}px`,
            backgroundColor: COLORS.white,
            borderRadius: BORDER_RADIUS.lg,
            border: `1px solid ${COLORS.border}`
          }}>
            <div style={{ marginBottom: SPACING.lg, color: COLORS.textSecondary }}>
              <McIcon icon="file" size="24" color={COLORS.textSecondary} />
            </div>
            <h3 className="mds-headline--x-small" style={{ color: COLORS.text, margin: `0 0 ${SPACING.md}px` }}>
              No Instructions Yet
            </h3>
            <p className="mds-text--medium-normal" style={{
              color: COLORS.textSecondary,
              maxWidth: '500px',
              margin: `0 auto ${SPACING.xxl}px`
            }}>
              Create your first instruction with a structured response format to start extracting structured data from your documents.
            </p>
            <MaerskButton onClick={handleCreateNew} variant="primary" size="large">
              + Create Your First Message
            </MaerskButton>
          </div>
        )}

        {/* Messages List */}
        {!loading && messages.length > 0 && (
          <>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: SPACING.md
            }}>
              {messages.map((message) => (
                <div
                  key={message.id}
                  onClick={() => handleEditMessage(message)}
                  style={{
                    backgroundColor: COLORS.white,
                    borderRadius: BORDER_RADIUS.md,
                    padding: SPACING.lg,
                    border: `1px solid ${COLORS.border}`,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: SPACING.lg
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = COLORS.backgroundLight;
                    e.currentTarget.style.borderColor = COLORS.primary;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = COLORS.white;
                    e.currentTarget.style.borderColor = COLORS.border;
                  }}
                >
                  {/* Icon */}
                  <div style={{ color: COLORS.text, minWidth: '40px', display: 'flex', justifyContent: 'center' }}>
                    <McIcon icon="file" />
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 className="mds-text--medium-bold" style={{ color: COLORS.text, margin: 0, marginBottom: SPACING.xs }}>
                      {message.name}
                    </h3>
                    {message.description && (
                      <p className="mds-text--small-normal" style={{
                        color: COLORS.textSecondary,
                        margin: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {message.description}
                      </p>
                    )}
                  </div>

                  {/* Metadata */}
                  <div className="mds-text--x-small-normal" style={{
                    color: COLORS.textSecondary,
                    textAlign: 'right',
                    minWidth: '150px'
                  }}>
                    <div>{formatDate(message.updated_at)}</div>
                    {message.created_by && (
                      <div style={{ marginTop: '2px' }}>{message.created_by}</div>
                    )}
                  </div>

                  {/* Favourite Toggle */}
                  <McButton
                    label={message.is_favourite ? 'Remove from favourites' : 'Add to favourites'}
                    hiddenlabel
                    icon={message.is_favourite ? 'star-solid' : 'star'}
                    appearance="neutral"
                    variant="plain"
                    click={(e) => toggleFavourite(e, message)}
                  />

                  {/* Arrow Icon */}
                  <div style={{ color: COLORS.textSecondary, display: 'flex' }}>
                    <McIcon icon="chevron-right" color={COLORS.textSecondary} />
                  </div>
                </div>
              ))}
            </div>

            {/* Load More */}
            {hasMore && (
              <div style={{
                textAlign: 'center',
                marginTop: SPACING.xxxl
              }}>
                <MaerskButton
                  onClick={() => loadMessages(false)}
                  variant="outline"
                  size="medium"
                >
                  Load More
                </MaerskButton>
              </div>
            )}
          </>
        )}
      </div>
      </div>{/* end content column */}
      </div>{/* end body flex */}
    </div>

  </>
  );
};

export default SystemMessageManager;
